import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { ALL_ITEMS } from "@/config/modules";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ResourceListPage from "@/pages/ResourceListPage";
import Products from "@/pages/Products";
import Costing from "@/pages/Costing";
import CompanyProfile from "@/pages/CompanyProfile";
import Reports from "@/pages/Reports";
import ProductionCalendar from "@/pages/ProductionCalendar";
import Recipes from "@/pages/Recipes";
import RecipeReview from "@/pages/RecipeReview";
import ProductionPlanDetail from "@/pages/ProductionPlanDetail";
import SalesBillDetail from "@/pages/SalesBillDetail";
import PurchaseOrders from "@/pages/PurchaseOrders";
import BuyerPoOrder from "@/pages/BuyerPoOrder";
import TallyUpload from "@/pages/TallyUpload";
import CleanupPanel from "@/pages/CleanupPanel";
import ProductionPlan from "@/pages/ProductionPlan";
import ProductionBatches from "@/pages/ProductionBatches";
import FinishedGoods from "@/pages/FinishedGoods";
import QualityAudits from "@/pages/QualityAudits";
import Capa from "@/pages/Capa";
import DispatchPreview from "@/pages/DispatchPreview";
import SupplierBillUpload from "@/pages/SupplierBillUpload";
import HistoricalDataImport from "@/pages/HistoricalDataImport";
import BuyerLedger from "@/pages/BuyerLedger";
import CustomerReceipts from "@/pages/CustomerReceipts";
import HistoricalStock from "@/pages/HistoricalStock";
import ReportSchedules from "@/pages/ReportSchedules";
import MasterDataSetup from "@/pages/MasterDataSetup";
import SalesBills from "@/pages/SalesBills";
import OrdersModule from "@/pages/OrdersModule";
import PurchaseBills from "@/pages/PurchaseBills";
import SupplierStatement from "@/pages/SupplierStatement";
import DispatchModule from "@/pages/DispatchModule";
import DailyAudit from "@/pages/DailyAudit";
import WeeklyAudit from "@/pages/WeeklyAudit";
import SettingsIngredients from "@/pages/SettingsIngredients";
import SalesReports from "@/pages/SalesReports";
import PurchaseReports from "@/pages/PurchaseReports";
import DuplicateAudit from "@/pages/DuplicateAudit";
import SalesReturns from "@/pages/SalesReturns";
import CreditNotes from "@/pages/CreditNotes";
import StoreInventory from "@/pages/StoreInventory";
import Users from "@/pages/Users";
import RecipeApprovals from "@/pages/RecipeApprovals";
import AuditReports from "@/pages/AuditReports";
import ReceivablesDue from "@/pages/ReceivablesDue";

function renderCustom(item) {
  switch (item.custom) {
    case "dashboard":
      return <Dashboard />;
    case "products":
      return <Products />;
    case "costing":
      return <Costing />;
    case "company-profile":
      return <CompanyProfile />;
    case "reports":
      return <Reports />;
    case "production-calendar":
      return <ProductionCalendar />;
    case "recipes":
      return <Recipes />;
    case "purchase-orders":
      return <PurchaseOrders />;
    case "orders":
      return <OrdersModule />;
    case "sales-bills":
      return <SalesBills />;
    case "purchase-bills":
      return <PurchaseBills />;
    case "dispatch":
      return <DispatchModule />;
    case "daily-audit":
      return <DailyAudit />;
    case "weekly-audit":
      return <WeeklyAudit />;
    case "settings-ingredients":
      return <SettingsIngredients />;
    case "sales-reports":
      return <SalesReports />;
    case "purchase-reports":
      return <PurchaseReports />;
    case "duplicate-audit":
      return <DuplicateAudit />;
    case "tally-upload":
      return <TallyUpload />;
    case "buyer-po-upload":
      return <BuyerPoOrder />;
    case "verification-cleanup":
      return <CleanupPanel />;
    case "production-plans":
      return <ProductionPlan />;
    case "production-batches":
      return <ProductionBatches />;
    case "finished-goods":
      return <FinishedGoods />;
    case "quality-audits":
      return <QualityAudits />;
    case "quality-audit-report":
      return <AuditReports mode="quality" />;
    case "audit-action-report":
      return <AuditReports mode="action" />;
    case "capa":
      return <Capa />;
    case "dispatch-preview":
      return <DispatchPreview />;
    case "supplier-bill-upload":
      return <SupplierBillUpload />;
    case "supplier-statement":
      return <SupplierStatement />;
    case "historical-data-import":
      return <HistoricalDataImport />;
    case "buyer-ledger":
      return <BuyerLedger />;
    case "customer-receipts":
      return <CustomerReceipts />;
    case "sales-returns":
      return <SalesReturns />;
    case "credit-notes":
      return <CreditNotes />;
    case "receivables-due":
      return <ReceivablesDue />;
    case "store-inventory":
      return <StoreInventory />;
    case "recipe-approvals":
      return <RecipeApprovals />;
    case "users":
      return <Users />;
    case "historical-stock":
      return <HistoricalStock />;
    case "report-schedules":
      return <ReportSchedules />;
    case "master-data-setup":
      return <MasterDataSetup />;
    default:
      return <ResourceListPage />;
  }
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Full-screen detail pages (outside the sidebar layout, like the mobile app) */}
            <Route path="/recipes/:id" element={<ProtectedRoute><RecipeReview /></ProtectedRoute>} />
            <Route path="/production-plans/:id" element={<ProtectedRoute><ProductionPlanDetail /></ProtectedRoute>} />
            <Route path="/sales-bills/:id" element={<ProtectedRoute><SalesBillDetail /></ProtectedRoute>} />
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              {ALL_ITEMS.map((item) => (
                <Route
                  key={item.path}
                  path={item.path}
                  element={
                    <ProtectedRoute>
                      {item.custom ? renderCustom(item) : <ResourceListPage />}
                    </ProtectedRoute>
                  }
                />
              ))}
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
